"use client"
import isEqual from "lodash-es/isEqual"
import map from "lodash-es/map"
import dynamic from "next/dynamic"
import { useTranslation } from "next-i18next"
import { FC, Fragment, useEffect, useState, useRef } from "react"

import { ContentContainer } from "../../components/ContentContainer"

import styles from "./PostList.module.scss"

import { Animated } from "@/components/common/animated"
import { Skeleton } from "@/components/common/skeleton"

import { client } from "@/config/apollo"
import { create } from "@/helpers/bem"
import { GET_POSTS_BY_CATEGORY_SLUG } from "@/queries/blog/posts"
import type {
  PostsByCategorySlugQuery,
  Category,
  ArticleEntity,
  Article,
} from "@/types/generated/strapi/graphql"
import { REVALIDATION_TAGS } from "@/utils/enviroment"

const Headline = dynamic(
  () =>
    import("@/components/common/headline").then(({ Headline }) => ({
      default: Headline,
    })),
  {
    ssr: false,
    loading: () => <Skeleton variant="text" />,
  },
)

const PostListItem = dynamic(
  () =>
    import("../PostListItem").then(({ PostListItem }) => ({
      default: PostListItem,
    })),
  {
    ssr: false,
    loading: () => <Skeleton className={bem("loading")} height={300} />,
  },
)

const bem = create(styles, "PostList")

export type PostListProps = {
  articles: ArticleEntity[]
  category: Category
  articleTotal?: number
}

export const PostList: FC<PostListProps> = ({
  category,
  articleTotal = 0,
  ...props
}) => {
  const [page, setPage] = useState<number>(1)
  const [articles, setArticles] = useState<ArticleEntity[]>(
    props?.articles ?? [],
  )
  const [loading, setLoading] = useState(false)
  const lastArticleRef = useRef<HTMLDivElement>(null)
  const observer = useRef<IntersectionObserver | null>(null)
  const { i18n } = useTranslation()
  const locale = i18n.language

  useEffect(() => {
    const loadArticles = async () => {
      setLoading(true)
      const { data } = await client().query<PostsByCategorySlugQuery>({
        query: GET_POSTS_BY_CATEGORY_SLUG,
        variables: {
          locale,
          slug: category?.slug,
          pagination: {
            page,
            pageSize: 10,
          },
        },
        context: {
          fetchOptions: {
            next: REVALIDATION_TAGS.articles,
          },
        },
      })
      setArticles((prev) => [
        ...prev,
        ...((data?.articles?.data ?? []) as ArticleEntity[]),
      ])
      setLoading(false)
    }
    if (isEqual(page, 1)) return
    loadArticles()
  }, [locale, page, category?.slug])

  useEffect(() => {
    if (loading || articles?.length >= articleTotal) return
    if (observer.current) observer.current?.disconnect()

    observer.current = new IntersectionObserver((entries) => {
      if (entries?.[0]?.isIntersecting) setPage((prev) => prev + 1)
    })

    if (lastArticleRef.current) observer.current.observe(lastArticleRef.current)
  }, [loading, articles?.length, articleTotal])

  const categoryName = category?.name
  return (
    <ContentContainer contentClassName={bem("container")}>
      <div className={bem("header")}>
        <Animated>
          <Headline highlight size="lg" type="h1" variant="primary">
            {articleTotal && articleTotal > 0
              ? `${categoryName} (${articleTotal})`
              : categoryName}
          </Headline>
          <Headline size="sm" type="h2">
            {category?.description}
          </Headline>
        </Animated>
      </div>
      {map(articles, ({ id, attributes }: ArticleEntity, index: number) => (
        <Fragment key={id}>
          <PostListItem {...(attributes as Article)} />
          {isEqual(index, articles.length - 1) && (
            <div ref={lastArticleRef} style={{ height: 1 }} />
          )}
        </Fragment>
      ))}
      {loading && (
        <Skeleton
          className={bem("loading")}
          height={300}
          variant="rectangular"
        />
      )}
    </ContentContainer>
  )
}
